import { LoadingButton } from "@mui/lab";
import { Link, Stack, Typography } from "@mui/material";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import confirm from "../../utils/account/confirm";
import addGTMEvent from "../../utils/gtm";
import CodeInput from "./components/code-input/CodeInput";
import ResendCode from "./components/resend-code/ResendCode";
import StepTitleView from "../StepTitleView";

/**
 * Log to GTM when subscrition is in sandbox
 */
const logSandboxAccount = (email: string) => {
  addGTMEvent({
    event: "sandboxAccount",
    enhanced_conversion_data: {
      email: email,
    },
  });
};

interface AccountVerificationProps {
  email: string;
  password: string;
  onSubmit: () => void;
}

const AccountVerification = ({
  email,
  password,
  onSubmit,
}: AccountVerificationProps) => {
  const { t } = useTranslation();
  const [verificationCode, setVerificationCode] = useState("");
  const [resendCodeDialogOpen, setResendCodeDialogOpen] = useState(false);
  // Code does match required format
  const [verificationCodeInputValid, setVerificationCodeInputValid] =
    useState(false);
  // Code does not match required format
  const [verificationCodeInputIncorrect, setVerificationCodeIncorrect] =
    useState(false);
  // Backend says code is not valid
  const [verificationCodeInvalid, setVerificationCodeInvalid] = useState(false);
  const [verificationCodeInvalidMessage, setVerificationCodeInvalidMessage] =
    useState<undefined | string>();
  const [formSubmitting, setFormSubmitting] = useState<boolean>(false);
  const verificationCodeRef = useRef<HTMLInputElement>(null);

  const onFormSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    submitForm();
  };
  const submitForm = async () => {
    setFormSubmitting(true);
    const verificationCode = verificationCodeRef.current?.value ?? "";
    try {
      await confirm(email, verificationCode, password);
      logSandboxAccount(email);
      onSubmit();
    } catch (e: any) {
      setVerificationCodeInvalid(true);
      setVerificationCodeInvalidMessage(e.code);
    } finally {
      setFormSubmitting(false);
    }
  };

  const onBlurTextField = (ref: React.RefObject<HTMLInputElement>) => () => {
    if (!ref.current) {
      return;
    }
    const valid = ref.current.checkValidity();
    const value = ref.current.value;

    if (value.length === 0) {
      setVerificationCodeIncorrect(false);
      return;
    }
    setVerificationCodeIncorrect(!valid);
    setVerificationCodeInvalidMessage("invalid");
  };

  const onChangeVerificationCodeField =
    (ref: React.RefObject<HTMLInputElement>) => () => {
      if (!ref.current) {
        return;
      }
      const valid = ref.current.checkValidity();
      const value = ref.current.value;
      setVerificationCodeInputValid(valid);
      setVerificationCode(value);
      if (valid && value.length === 6) {
        submitForm();
      }
    };

  const onClickResendVerificationCode = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    setResendCodeDialogOpen(true);
  };

  const submitDisabled = !verificationCodeInputValid;

  return (
    <>
      <form autoComplete="off" noValidate onSubmit={onFormSubmit}>
        <StepTitleView
          title={t("step.accountVerification.verificationCode.label")}
          descriptions={[
            t("step.accountVerification.description.partA", { email }),
            t("step.accountVerification.description.partB"),
          ]}
        />
        <Stack spacing={2} mb={12} pt={2}>
          <CodeInput
            ref={verificationCodeRef}
            code={verificationCode}
            error={verificationCodeInvalid || verificationCodeInputIncorrect}
            label={t("step.accountVerification.verificationCode.label")}
            errorMessage={t(
              `step.accountVerification.verificationCode.error.${verificationCodeInvalidMessage}`
            )}
            onChange={onChangeVerificationCodeField(verificationCodeRef)}
            onBlur={onBlurTextField(verificationCodeRef)}
          />
          <Typography variant="caption">
            <Link
              id="account-verification-resend-link"
              variant="caption"
              onClick={onClickResendVerificationCode}
            >
              {t("step.accountVerification.notSeeingEmailHint.partB")}
            </Link>
          </Typography>
        </Stack>
        <LoadingButton
          id="account-verification-continue"
          type="submit"
          variant="contained"
          disabled={submitDisabled}
          loading={formSubmitting}
          fullWidth
        >
          {t("button.next")}
        </LoadingButton>
      </form>
      <ResendCode
        open={resendCodeDialogOpen}
        email={email}
        onClose={() => setResendCodeDialogOpen(false)}
      />
    </>
  );
};

export default AccountVerification;
